import { useInfiniteQuery } from '@tanstack/react-query';
import {
  diagnosisWords,
  REPORT_PERIOD_LIMIT_DAYS,
  type LabRegisterRow,
  type OpdRegisterRow,
  type ReportPeriod,
} from '@wodi/shared';
import { Button, PageHeader, PrintTable, Select, TextField } from '@wodi/ui';
import { useState, type ReactNode } from 'react';

import { screen, TableScroll } from '../layout/screen.tsx';
import { fetchLabRegister, fetchOpdRegister, reportKeys } from '../api/reports.ts';
import { formatInstant } from '../patients/format.ts';
import { plural } from '../reports/labels.ts';
import {
  AsAt,
  BackToReports,
  PeriodFields,
  periodIsValid,
  periodWords,
  PrintableReport,
  ReportActions,
  ReportStates,
  todayIso,
  useSheet,
} from '../reports/report-frame.tsx';
import styles from '../reports/reports.module.css';

// The OPD and lab registers (plan §2.8, §2.9): the facility the reader is working at, up to
// 31 days (P8). They list patients, so the server leaves out visits with sensitivity labels
// the reader isn't granted and audits every view; downloads warn before they start. Rows
// wrap to a second line instead of scrolling sideways at 1366x768.

export const PAGE_SIZE = 100;

const VISIT_TYPE_OPTIONS = [
  { id: 'any', label: 'Any visit type' },
  { id: 'outpatient', label: 'Outpatient' },
  { id: 'walk_in', label: 'Walk-in' },
];

const VISIT_STATUS_WORDS = { open: 'Open', closed: 'Closed', cancelled: 'Cancelled' } as const;

export function RegisterShell({
  title,
  filters,
  children,
}: {
  title: string;
  filters: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className={styles.page}>
      <BackToReports />
      <PageHeader title={title} />
      {filters}
      <div className={screen.panel}>{children}</div>
    </div>
  );
}

export function MoreButton({
  hasMore,
  loading,
  onMore,
}: {
  hasMore: boolean;
  loading: boolean;
  onMore: () => void;
}) {
  if (!hasMore) return null;
  return (
    <div>
      <Button onPress={onMore} isPending={loading} pendingLabel="Loading">
        Show more
      </Button>
    </div>
  );
}

// --- OPD register --------------------------------------------------------------------------

export function OpdRegisterPage({ day }: { day?: string | undefined }) {
  const start = day ?? todayIso();
  const [period, setPeriod] = useState<ReportPeriod>({ from: start, to: start });
  const [visitType, setVisitType] = useState('any');
  const [diagnosisCode, setDiagnosisCode] = useState('');
  const [shown, setShown] = useState<{
    period: ReportPeriod;
    visitType?: 'outpatient' | 'walk_in';
    diagnosisCode?: string;
  }>({ period });
  const register = useInfiniteQuery({
    queryKey: reportKeys.report('opd-register', shown),
    queryFn: ({ pageParam }) =>
      fetchOpdRegister({
        ...shown,
        limit: PAGE_SIZE,
        ...(pageParam === null ? {} : { cursor: pageParam }),
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });
  const sheet = useSheet();
  const valid = periodIsValid(period, REPORT_PERIOD_LIMIT_DAYS.register);
  const first = register.data?.pages[0];
  const rows = register.data?.pages.flatMap((p) => p.rows) ?? [];
  return (
    <RegisterShell
      title="OPD register"
      filters={
        <form
          className={styles.filters}
          onSubmit={(e) => {
            e.preventDefault();
            if (!valid) return;
            const code = diagnosisCode.trim();
            setShown({
              period,
              ...(visitType === 'outpatient' || visitType === 'walk_in' ? { visitType } : {}),
              ...(code === '' ? {} : { diagnosisCode: code }),
            });
          }}
        >
          <PeriodFields
            period={period}
            onChange={setPeriod}
            maxDays={REPORT_PERIOD_LIMIT_DAYS.register}
          />
          <Select
            label="Visit type"
            options={VISIT_TYPE_OPTIONS}
            value={visitType}
            onChange={(v) => {
              setVisitType(String(v));
            }}
          />
          <TextField
            label="Diagnosis code"
            description="Exact code, for example as printed on the claim."
            value={diagnosisCode}
            onChange={setDiagnosisCode}
            dense
          />
          <div>
            <Button type="submit" variant="primary" isDisabled={!valid}>
              Show report
            </Button>
          </div>
        </form>
      }
    >
      <ReportStates
        query={register}
        what="register"
        isEmpty={(d) => (d.pages[0]?.rows.length ?? 0) === 0}
        emptyText={`No visits on ${periodWords(shown.period)}.`}
      >
        {(d) => (
          <>
            <AsAt dataAsAt={d.pages[0]?.dataAsAt ?? new Date().toISOString()} />
            {d.pages[0]?.diagnosesAvailable === false ? (
              <p className={styles.muted}>Diagnoses appear here once consultations record them.</p>
            ) : null}
            <p className={styles.muted}>
              {`${plural(rows.length, 'visit', 'visits')} shown${register.hasNextPage ? ', more to load' : ''}.`}
            </p>
            <TableScroll>
              <table className={styles.table} aria-label="OPD register">
                <OpdHead />
                <tbody>
                  {rows.map((r) => (
                    <OpdRow key={r.visitId} row={r} />
                  ))}
                </tbody>
              </table>
            </TableScroll>
            <MoreButton
              hasMore={register.hasNextPage}
              loading={register.isFetchingNextPage}
              onMore={() => void register.fetchNextPage()}
            />
          </>
        )}
      </ReportStates>
      {first === undefined ? null : (
        <>
          <ReportActions
            reportKey="opd-register"
            sheet={sheet}
            printBody={{
              period: first.period,
              facilityIds: [first.facility.facilityId],
              rowCount: rows.length,
            }}
            exportBody={shown}
            disabled={register.isFetching}
          />
          <PrintableReport
            sheet={sheet}
            title="OPD register"
            facility={first.facility.name}
            period={periodWords(first.period)}
            dataAsAt={first.dataAsAt}
          >
            <PrintTable>
              <OpdHead />
              <tbody>
                {rows.map((r) => (
                  <OpdRow key={r.visitId} row={r} />
                ))}
              </tbody>
            </PrintTable>
          </PrintableReport>
        </>
      )}
    </RegisterShell>
  );
}

function OpdHead() {
  return (
    <thead>
      <tr>
        <th scope="col">Arrived</th>
        <th scope="col">Patient</th>
        <th scope="col">Sex and age</th>
        <th scope="col">Payer</th>
        <th scope="col">Seen at</th>
        <th scope="col">Status</th>
      </tr>
    </thead>
  );
}

function OpdRow({ row: r }: { row: OpdRegisterRow }) {
  return (
    <tr>
      <td>
        {formatInstant(r.startedAt)}
        <span className={`${styles.secondLine} wodi-id`}>{r.visitNumber}</span>
      </td>
      <td>
        {r.patientName}
        <span className={`${styles.secondLine} wodi-id`}>{r.patientNumber}</span>
        {r.residence === null ? null : <span className={styles.secondLine}>{r.residence}</span>}
        {r.diagnoses === null || r.diagnoses.length === 0 ? null : (
          <span className={styles.secondLine}>{r.diagnoses.map(diagnosisWords).join('; ')}</span>
        )}
      </td>
      <td>
        {r.sex}
        <span className={styles.secondLine}>
          {r.age === null ? 'Age not recorded' : `${r.age}${r.ageEstimated ? ' (estimated)' : ''}`}
        </span>
      </td>
      <td>{r.payers.join(', ')}</td>
      <td>{r.servicePoints.join(', ')}</td>
      <td>
        {VISIT_STATUS_WORDS[r.status]}
        <span className={styles.secondLine}>{r.type === 'walk_in' ? 'Walk-in' : 'Outpatient'}</span>
      </td>
    </tr>
  );
}

// --- Lab register --------------------------------------------------------------------------

const ORDER_STATUS_WORDS = {
  active: 'In progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
  entered_in_error: 'Entered in error',
} as const;

const REPORT_STATUS_WORDS: Record<string, string> = {
  in_progress: 'Results being entered',
  awaiting_validation: 'Awaiting validation',
  technically_validated: 'Awaiting clinical validation',
  final: 'Released',
  superseded: 'Corrected',
};

export function LabRegisterPage({ day }: { day?: string | undefined }) {
  const start = day ?? todayIso();
  const [period, setPeriod] = useState<ReportPeriod>({ from: start, to: start });
  const [shown, setShown] = useState<{ period: ReportPeriod }>({ period });
  const register = useInfiniteQuery({
    queryKey: reportKeys.report('lab-register', shown),
    queryFn: ({ pageParam }) =>
      fetchLabRegister({
        ...shown,
        limit: PAGE_SIZE,
        ...(pageParam === null ? {} : { cursor: pageParam }),
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });
  const sheet = useSheet();
  const valid = periodIsValid(period, REPORT_PERIOD_LIMIT_DAYS.register);
  const first = register.data?.pages[0];
  const rows = register.data?.pages.flatMap((p) => p.rows) ?? [];
  return (
    <RegisterShell
      title="Lab register"
      filters={
        <form
          className={styles.filters}
          onSubmit={(e) => {
            e.preventDefault();
            if (valid) setShown({ period });
          }}
        >
          <PeriodFields
            period={period}
            onChange={setPeriod}
            maxDays={REPORT_PERIOD_LIMIT_DAYS.register}
          />
          <div>
            <Button type="submit" variant="primary" isDisabled={!valid}>
              Show report
            </Button>
          </div>
        </form>
      }
    >
      <p className={styles.muted}>Flags only. Result values stay in the patient's record.</p>
      <ReportStates
        query={register}
        what="register"
        isEmpty={(d) => (d.pages[0]?.rows.length ?? 0) === 0}
        emptyText={`No lab tests requested on ${periodWords(shown.period)}.`}
      >
        {(d) => (
          <>
            <AsAt dataAsAt={d.pages[0]?.dataAsAt ?? new Date().toISOString()} />
            <TableScroll>
              <table className={styles.table} aria-label="Lab register">
                <LabHead />
                <tbody>
                  {rows.map((r) => (
                    <LabRow key={r.orderId} row={r} />
                  ))}
                </tbody>
              </table>
            </TableScroll>
            <MoreButton
              hasMore={register.hasNextPage}
              loading={register.isFetchingNextPage}
              onMore={() => void register.fetchNextPage()}
            />
          </>
        )}
      </ReportStates>
      {first === undefined ? null : (
        <>
          <ReportActions
            reportKey="lab-register"
            sheet={sheet}
            printBody={{
              period: first.period,
              facilityIds: [first.facility.facilityId],
              rowCount: rows.length,
            }}
            exportBody={shown}
            disabled={register.isFetching}
          />
          <PrintableReport
            sheet={sheet}
            title="Lab register"
            facility={first.facility.name}
            period={periodWords(first.period)}
            dataAsAt={first.dataAsAt}
          >
            <PrintTable>
              <LabHead />
              <tbody>
                {rows.map((r) => (
                  <LabRow key={r.orderId} row={r} />
                ))}
              </tbody>
            </PrintTable>
          </PrintableReport>
        </>
      )}
    </RegisterShell>
  );
}

function LabHead() {
  return (
    <thead>
      <tr>
        <th scope="col">Requested</th>
        <th scope="col">Patient</th>
        <th scope="col">Test</th>
        <th scope="col">Sample</th>
        <th scope="col">Status</th>
        <th scope="col" data-numeric>
          Turnaround
        </th>
        <th scope="col">Flags</th>
      </tr>
    </thead>
  );
}

function flagWords(row: LabRegisterRow): string {
  if (row.critical) return 'Critical';
  if (row.abnormalCount > 0)
    return plural(row.abnormalCount, 'abnormal result', 'abnormal results');
  return '';
}

function LabRow({ row: r }: { row: LabRegisterRow }) {
  return (
    <tr>
      <td>{formatInstant(r.orderedAt)}</td>
      <td>
        {r.patientName}
        <span className={`${styles.secondLine} wodi-id`}>{r.patientNumber}</span>
      </td>
      <td>
        {r.testName}
        <span className={styles.secondLine}>{r.sectionName}</span>
      </td>
      <td>
        <span className="wodi-id">{r.sampleNumbers.join(', ')}</span>
        <span className={styles.secondLine}>
          {r.collectedAt === null ? 'Not collected' : `Collected ${formatInstant(r.collectedAt)}`}
        </span>
      </td>
      <td>
        {r.reportStatus === null
          ? ORDER_STATUS_WORDS[r.orderStatus]
          : (REPORT_STATUS_WORDS[r.reportStatus] ?? ORDER_STATUS_WORDS[r.orderStatus])}
        {r.releasedAt === null ? null : (
          <span className={styles.secondLine}>{`Released ${formatInstant(r.releasedAt)}`}</span>
        )}
      </td>
      <td data-numeric>
        {r.turnaroundMinutes === null ? '' : `${String(r.turnaroundMinutes)} min`}
      </td>
      <td className={r.critical ? styles.critical : r.abnormalCount > 0 ? styles.flag : undefined}>
        {flagWords(r)}
      </td>
    </tr>
  );
}
